import styled, { css } from 'styled-components';
import type { ThemeProps } from 'styled-components';
import type { AllColorType, ThemeType } from '../../util/types';
import type { InputHTMLAttributes } from 'react';
import {
  CHECKBOX_SIZE,
  DEFAULT_TRANSITION_DURATION,
  DEFAULT_TRANSITION_FUNC,
  getPolishedColors,
} from '../../util/styled';
import { Z_INDEXES } from '../../util/vars';
import { TOGGLE_SLIDER } from '../../util/data-id';

const getFillStyles = (props: ThemeProps<ThemeType> & {
  $color: AllColorType,
  $isOnOff: boolean,
  $isChecked: boolean,
  $isDisabled: boolean
}) => {
  const colors = getPolishedColors(props);
  if (props.$isDisabled) {
    return css`
      background-color: ${props.theme.colors.light};
      [data-id='${TOGGLE_SLIDER}'] {
        background-color: ${props.theme.colors.muted};
      }
    `;
  }

  if (props.$isOnOff && !props.$isChecked) {
    return css`
      background-color: ${colors.light.color};
      [data-id='${TOGGLE_SLIDER}'] {
        background-color: ${props.theme.colors.muted};
      }    
    `;
  }

  return css`
    background-color: ${colors.light.color};
    [data-id='${TOGGLE_SLIDER}'] {
      background-color: ${colors.main.color};
    }
  `;
};

export const ToggleWrapStyled = styled('div')<{
  $color: AllColorType,
  $isOnOff: boolean,
  $isChecked: boolean,
  $isDisabled: boolean,
  $isInverted?: boolean,
}>`
  position: relative;
  display: flex;
  width: ${CHECKBOX_SIZE * 2}px;
  height: ${CHECKBOX_SIZE}px;  
  border-radius: ${(props) => props.theme.radius.circle};
  
  ${(props) => getFillStyles(props)}
  
  [data-id='${TOGGLE_SLIDER}'] {
    position: absolute;
    z-index: ${Z_INDEXES.level_1};
    top: 0;
    bottom: 0;
    left: -3px;    
    margin: auto;
    width: ${CHECKBOX_SIZE * 1.25}px;
    height: ${CHECKBOX_SIZE * 1.25}px;
    border-radius: ${(props) => props.theme.radius.circle};
    transition: all ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC};
    
    &[data-animation='true'] {
      animation: ${(props) => !props.$isInverted ? 'off' : 'on'} ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC} forwards;
    }   
    &[data-animation='false'] {
      animation: ${(props) => !props.$isInverted ? 'on' : 'off'} ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC} forwards;
    }
    
    @keyframes on {
      0% {
        transform: translateX(100%);
      }
      100% {
        transform: translateX(0));
      }
    }
  
    @keyframes off {
      0% {
        transform: translateX(0});
      }
      100% {
        transform: translateX(100%);
      }
    }         
  }
`;

export const ToggleStyled = styled('input').attrs((props: InputHTMLAttributes<HTMLInputElement>) => ({
  name: props.name || props.id,
  required: props.required || false,
  checked: props.checked,
  disabled: props.disabled,
  type: 'checkbox',
}))`
  appearance: none!important;
  padding: 0!important;
  margin: 0!important;  
  box-sizing: border-box; 
  color: transparent !important;
  border-color: transparent !important;
  border-radius: ${(props) => props.theme.radius.circle};
  
  transition: box-shadow ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC};
  
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;   
  z-index: ${Z_INDEXES.level_1 + 1};
  
  width: 100%;
  height: 100%;   
  
  ${(props) => !props.disabled && 'cursor: pointer;'}  
  
  &,
  &:hover,
  &:checked,
  &:disabled,
  &[readonly] {
    background-image: none !important;
    background-color: transparent !important;
  }
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;
